import { type Syllable, readWord, writeSyllable } from "@kensio/pinyinjs";

import { finishedSyllable } from "../site/fuxi-typed-syllable.js";
import { toneMarked } from "../site/fuxi-tone-numbers.js";

/**
 * Reading the pinyin chart's playground, a field a reader types pinyin into
 * and hears back.
 *
 * It types the way a 复习 reading card does. `ni3` becomes `nǐ` as the 3
 * lands, and a syllable typed with its mark on is finished once it cannot
 * grow any longer. `src/site/fuxi-typed-syllable.ts` decides that, and this
 * module only says what to play once it has.
 *
 * **A finished syllable plays the word it ends.** A word is a run of pinyin
 * with no space in it, so typing `ni3hao3` plays nǐ on the 3 and then nǐhǎo on
 * the second 3. A reader hears each syllable on its own as they learn it and
 * the whole word once it is there.
 *
 * **A syllable plays as the 词典 recorded it.** The recordings are one per
 * syllable and tone, so nǐhǎo plays two third tones, where a speaker says the
 * first as a second. The page says so.
 *
 * **A neutral tone finishes a word but not a syllable.** The chart has no
 * neutral recording of a syllable on its own, and the 词典 has whole words
 * with one in, `yi1ge5` and `xi3huan5`. So a 5 that ends a word of two
 * syllables or more plays the word, and a 5 on its own plays nothing.
 */

/** One syllable of what was typed, as it is drawn and as it is played. */
export interface PlaygroundSyllable {
  /** The syllable written in tone marks, `hǎo`. */
  readonly text: string;

  /**
   * The key a chart tone is filed under, `hao3` or `lv3`, or undefined for a
   * syllable with no tone or the neutral one, which the chart has no
   * recording of on its own.
   */
  readonly key: string | undefined;

  /**
   * The syllable as a word's recording spells it, `hao3` or `ge5`, with the
   * neutral tone written 5. Undefined for a syllable in a word with no tone
   * written anywhere, which could be any tone. A syllable with no mark in a
   * word with one elsewhere is neutral, the way pinyin writes `xǐhuan`.
   */
  readonly reading: string | undefined;

  /** The tone, 1 to 4, or 0 for none and the neutral tone. */
  readonly tone: number;
}

/** One word of what was typed: a run of pinyin with no space in it. */
export interface PlaygroundWord {
  /** The run as the field shows it. */
  readonly text: string;

  /** Its syllables, or none where the run is not pinyin. */
  readonly syllables: readonly PlaygroundSyllable[];
}

/** A run of anything a pinyin word can be spelt with. */
const wordRun = /[\p{L}\p{M}'’:1-5-]+/gu;

/** The run a text ends on. */
const lastRun = /[\p{L}\p{M}'’:1-5-]+$/u;

/** A syllable in numbers as the recordings are filed, `ü` written `v`. */
function numbered(syllable: Syllable): string {
  return writeSyllable({ ...syllable, erhua: false }, "numbers").replace(
    /ü/gu,
    "v",
  );
}

/**
 * One syllable of a word.
 * @param syllable - The syllable as read.
 * @param toned - Whether the word has a tone written on any syllable, which
 *   makes a syllable with none neutral.
 */
function syllableOf(syllable: Syllable, toned: boolean): PlaygroundSyllable {
  const tone = syllable.tone ?? 0;
  const charted = tone >= 1 && tone <= 4;

  return {
    text: writeSyllable(syllable, "marks"),
    key: charted ? numbered(syllable) : undefined,
    reading: charted
      ? numbered(syllable)
      : toned
        ? numbered({ ...syllable, tone: 5 })
        : undefined,
    tone: charted ? tone : 0,
  };
}

function wordOf(run: string): PlaygroundWord {
  const marked = toneMarked(run).normalize("NFC");
  const syllables = readWord(marked.toLowerCase().replaceAll("’", "'"));
  const toned =
    syllables?.some((syllable) => syllable.tone !== undefined) ?? false;

  return {
    text: marked,
    syllables: syllables?.map((syllable) => syllableOf(syllable, toned)) ?? [],
  };
}

/**
 * The reading a word's own recording is filed under, `bei3jing1` or
 * `yi1ge5`, if it could have one.
 *
 * Only a word of two syllables or more with every tone known has one to look
 * for. The 词典 records words, and a syllable on its own is the chart's.
 * @param word - A word of the field.
 * @returns The reading, or undefined.
 */
export function wordReading(word: PlaygroundWord): string | undefined {
  const readings = word.syllables.map((syllable) => syllable.reading);

  return readings.length < 2 || readings.includes(undefined)
    ? undefined
    : readings.join("");
}

/**
 * Read a whole field into words and syllables.
 * @param text - What the field holds.
 * @returns Its words, in order.
 */
export function readPlayground(text: string): readonly PlaygroundWord[] {
  return [...text.matchAll(wordRun)].map((match) => wordOf(match[0]));
}

/**
 * What a keystroke in the playground plays, if anything.
 *
 * @param typed - The field up to the caret as the keystroke left it, before
 *   any digit in it has been written as a mark.
 * @param sounded - What `through` was the last time this field played.
 * @returns The word the finished syllable ends, cut off after it, and what to
 *   hand back as `sounded` next time. Nothing where no syllable with a tone
 *   was finished, or where a neutral one was and ends no word with a
 *   recording to ask for.
 */
export function playgroundKeystroke(
  typed: string,
  sounded: string | undefined,
): { readonly word: PlaygroundWord; readonly through: string } | undefined {
  const finished = finishedSyllable(typed, sounded);

  if (finished === undefined) {
    return undefined;
  }

  const run = lastRun.exec(finished.through)?.[0];

  if (run === undefined) {
    return undefined;
  }

  const word = wordOf(run);
  const neutral = finished.key.endsWith("5");

  return word.syllables.length === 0 ||
    (neutral && wordReading(word) === undefined)
    ? undefined
    : { word, through: finished.through };
}
